import { useState } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PasswordInput } from "@/components/PasswordInput";
import { LAYOUT } from "@/constants/testIds";
import { X, AlertCircle, CheckCircle2, ShieldCheck } from "lucide-react";

const inputCls =
  "w-full border border-[#d9cfb8] bg-white px-3 py-2 text-[13px] rounded focus:border-[#c8a04a] focus:outline-none";

export function SecurityPinModal({ onClose }) {
  const { user, refresh } = useAuth();
  const hasPin = !!user?.has_pin;
  const [current, setCurrent] = useState("");
  const [pin, setPin] = useState("");
  const [confirm, setConfirm] = useState("");
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [busy, setBusy] = useState(false);

  const finish = async (msg) => {
    setSuccess(msg);
    await refresh();
    setTimeout(onClose, 1600);
  };

  const savePin = async (e) => {
    e.preventDefault();
    setError("");
    if (!/^\d{4,8}$/.test(pin)) {
      setError("El PIN debe tener entre 4 y 8 dígitos numéricos");
      return;
    }
    if (pin !== confirm) {
      setError("Los PIN no coinciden");
      return;
    }
    setBusy(true);
    try {
      await api.put("/auth/pin", { current_password: current, pin });
      await finish(hasPin ? "PIN actualizado correctamente." : "PIN activado. Se pedirá al iniciar sesión.");
    } catch (e2) {
      setError(e2?.response?.data?.detail || "Error al guardar el PIN");
    } finally {
      setBusy(false);
    }
  };

  const disablePin = async () => {
    setError("");
    if (!current) {
      setError("Ingrese su contraseña actual para desactivar el PIN");
      return;
    }
    setBusy(true);
    try {
      await api.delete("/auth/pin", { data: { current_password: current } });
      await finish("PIN desactivado.");
    } catch (e2) {
      setError(e2?.response?.data?.detail || "Error al desactivar el PIN");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
      <div
        data-testid={LAYOUT.pinModal}
        className="w-full max-w-md rounded border border-[#d9cfb8] bg-white p-6 shadow-xl"
      >
        <div className="flex items-center justify-between mb-2">
          <h3 className="font-serif text-lg text-[#1c1a14] flex items-center gap-2">
            <ShieldCheck size={18} className="text-[#c8a04a]" /> PIN de seguridad (2FA)
          </h3>
          <button onClick={onClose} className="text-[#8b7e5e] hover:text-[#1c1a14]">
            <X size={18} />
          </button>
        </div>

        <p className="text-[12px] text-[#5b5240] mb-4">
          {hasPin
            ? "Tiene un PIN activo: se pide después de la contraseña al iniciar sesión. Puede cambiarlo o desactivarlo."
            : "Active un PIN de 4 a 8 dígitos como segundo paso de verificación al iniciar sesión."}
        </p>

        {success ? (
          <div
            data-testid={LAYOUT.pinModalSuccess}
            className="flex items-center gap-2 rounded border border-emerald-300 bg-emerald-50 px-3 py-3 text-[13px] text-emerald-700"
          >
            <CheckCircle2 size={16} /> {success}
          </div>
        ) : (
          <form onSubmit={savePin} className="space-y-3">
            <label className="block">
              <span className="block text-[10px] uppercase tracking-widest text-[#8b7e5e] mb-1">
                Contraseña actual *
              </span>
              <PasswordInput
                testid={LAYOUT.pinCurrentPassword}
                className={inputCls}
                value={current}
                onChange={(e) => setCurrent(e.target.value)}
                autoFocus
                required
              />
            </label>
            <label className="block">
              <span className="block text-[10px] uppercase tracking-widest text-[#8b7e5e] mb-1">
                {hasPin ? "Nuevo PIN (4–8 dígitos)" : "PIN (4–8 dígitos)"} *
              </span>
              <PasswordInput
                testid={LAYOUT.pinInput}
                className={inputCls}
                value={pin}
                onChange={(e) => setPin(e.target.value.replace(/\D/g, "").slice(0, 8))}
                inputMode="numeric"
                required
              />
            </label>
            <label className="block">
              <span className="block text-[10px] uppercase tracking-widest text-[#8b7e5e] mb-1">
                Confirmar PIN *
              </span>
              <PasswordInput
                testid={LAYOUT.pinConfirmInput}
                className={inputCls}
                value={confirm}
                onChange={(e) => setConfirm(e.target.value.replace(/\D/g, "").slice(0, 8))}
                inputMode="numeric"
                required
              />
            </label>

            {error && (
              <div
                data-testid={LAYOUT.pinModalError}
                className="flex items-center gap-2 rounded border border-red-300 bg-red-50 px-3 py-2 text-[12px] text-red-700"
              >
                <AlertCircle size={14} /> {error}
              </div>
            )}

            <div className="flex justify-between gap-2 pt-2">
              {hasPin ? (
                <button
                  type="button"
                  data-testid={LAYOUT.pinDisable}
                  onClick={disablePin}
                  disabled={busy}
                  className="rounded border border-red-300 px-3 py-1.5 text-[12px] uppercase tracking-widest text-red-700 hover:bg-red-50 disabled:opacity-50"
                >
                  Desactivar PIN
                </button>
              ) : (
                <span />
              )}
              <div className="flex gap-2">
                <button
                  type="button"
                  onClick={onClose}
                  disabled={busy}
                  className="rounded border border-[#d9cfb8] px-3 py-1.5 text-[12px] uppercase tracking-widest text-[#5b5240] hover:bg-[#fbf7ee]"
                >
                  Cancelar
                </button>
                <button
                  type="submit"
                  data-testid={LAYOUT.pinSave}
                  disabled={busy}
                  className="rounded border border-[#c8a04a] bg-[#c8a04a] px-3 py-1.5 text-[12px] uppercase tracking-widest text-[#1c1a14] hover:bg-[#b8902a] disabled:opacity-50"
                >
                  {busy ? "Guardando..." : hasPin ? "Cambiar PIN" : "Activar PIN"}
                </button>
              </div>
            </div>
          </form>
        )}
      </div>
    </div>
  );
}
